import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { CheckCircle2, Loader2, ArrowRight, AlertCircle } from "lucide-react";

// Polled checkout-status page. Stripe redirects here after successful payment
// with ?session_id=cs_test_xxx — we poll the backend until payment_status="paid"
// (or the server times out / errors), then route to dashboard.
export default function BillingSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [err, setErr] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      setErr("Missing session_id");
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          setErr("Checkout session expired");
          return;
        }
        tries.current += 1;
        if (tries.current >= 8) {
          setState("timeout");
          return;
        }
        setTimeout(poll, 1500);
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || e.message || "Status check failed");
        setState("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <main className="max-w-md mx-auto px-6 py-20 text-center" data-testid="billing-success">
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto text-[#00E5FF] animate-spin" size={32} />
            <h1 className="font-display font-black text-2xl mt-6">Confirming payment…</h1>
            <p className="text-zinc-500 text-sm mt-2 font-mono">stripe → us · usually 2–3 seconds</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#00FF41]" size={48} />
            <h1 className="font-display font-black text-3xl mt-6" data-testid="billing-success-title">
              Welcome to Pro!
            </h1>
            <p className="text-zinc-300 mt-3 leading-relaxed">
              All 72 labs, unlimited AI hints, and full analytics are unlocked.
              Receipt is on its way to your email.
            </p>
            <button
              onClick={() => nav("/dashboard")}
              className="mt-8 px-6 py-3 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center gap-2 mx-auto"
              data-testid="billing-success-go"
            >
              go to dashboard <ArrowRight size={14} />
            </button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <AlertCircle className="mx-auto text-[#EF4444]" size={32} />
            <h1 className="font-display font-black text-2xl mt-6">
              {state === "timeout" ? "Still processing" : "Payment not confirmed"}
            </h1>
            <p className="text-zinc-400 mt-3 font-mono text-sm" data-testid="billing-success-err">
              {err || "Stripe is taking longer than usual. Refresh in a moment — if your card was charged, the upgrade is on its way."}
            </p>
            <button
              onClick={() => nav("/pricing")}
              className="mt-8 px-4 py-2 border border-zinc-700 hover:border-[#00E5FF] text-zinc-200 hover:text-[#00E5FF] text-sm font-mono uppercase tracking-widest transition-colors"
            >
              back to pricing
            </button>
          </>
        )}
      </main>
    </div>
  );
}
